import { CoreButton, CoreCheckbox, CoreModalDialogFooter, CoreSelect } from '@components'
import DeleteIcon from '@images/icons/delete-icon.svg?react'
import PlusIcon from '@images/icons/plus.svg?react'
import { formatCurrencyForDisplay, toastSuccess } from '@utils'
import { selectMerchantCurrency, useAppSelector } from 'mmfintech-backend-api'
import type {
  ApprovalLevelResponse,
  ApprovalMatrixLevelApproverResponse,
  ApprovalMatrixRequest,
  ApprovalMatrixResponse,
  DepartmentResponse,
  PortalMerchantUserResponse,
  SelectOptions
} from 'mmfintech-backend-api/types'
import { GlobalContext, getCurrencySymbol, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { Threshold } from './Threshold'

import './baseApprovalMatrixModal.scss'

type SelectValue = string | number | { label: string; value: number }

type Approvers = {
  amountThreshold?: number | string
  priority: number
  requiredApprovals: number
  approvers: number[]
  isThresholdEnabled?: boolean
  id: string | number
}

const DEFAULT_THRESHOLD = 999999999999

const INITIAL_APPROVERS_DATA = () => ({
  priority: 1,
  requiredApprovals: 1,
  approvers: [],
  isThresholdEnabled: false,
  id: uuid()
})

const NUMBER_MAP = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th'
}

const normalizeAmountThreshold = (threshold?: number | string): number | undefined => {
  if (threshold === undefined) return undefined
  return typeof threshold === 'string' ? parseFloat(threshold) : threshold
}

const normalizeToNumberArray = (value: SelectValue | SelectValue[]): number[] => {
  const extractNumber = (v: SelectValue): number => {
    if (typeof v === 'number') return v
    if (typeof v === 'string') return parseInt(v, 10)
    if (typeof v === 'object' && v !== null && 'value' in v) return v.value
    return 0
  }

  if (!Array.isArray(value)) {
    return [extractNumber(value)]
  }

  const numbers = value.map(extractNumber)
  const uniqueNumbers = Array.from(new Set(numbers))

  return uniqueNumbers
}

const recalculatePriorities = (data: Approvers[]): Approvers[] => {
  return data.map((item, index) => ({ ...item, priority: index + 1 }))
}

type Props = {
  // Department and matrix data
  department?: DepartmentResponse
  matrix?: ApprovalMatrixResponse
  reload?: () => void

  // Department dropdown configuration
  departmentsOptions?: SelectOptions
  departmentsLoading?: boolean

  // Users configuration
  users: PortalMerchantUserResponse[]
  usersFetching: boolean

  approversTooltip: React.ReactNode
  selectUserDropdownHint: React.ReactNode

  // API mutation hooks
  createMutation: (
    params: ApprovalMatrixRequest | { departmentId: string | number; data: ApprovalMatrixRequest }
  ) => Promise<ApprovalMatrixResponse | undefined>
  updateMutation: (
    params: ApprovalMatrixRequest | { departmentId: string | number; data: ApprovalMatrixRequest }
  ) => Promise<ApprovalMatrixResponse | undefined>
  isCreating?: boolean
  isUpdating?: boolean

  // Error handling - consolidated array of all errors
  errors?: unknown[]

  // Delete modal component
  DeleteModalComponent?: React.ComponentType<{ reload?: () => void; department?: DepartmentResponse }>

  // Customization options
  successMessage?: {
    title: string
    subtitle?: string
  }

  // Transform functions for data mapping
  transformApproversToPayload?: (
    approvers: Approvers[],
    departmentId?: string | number
  ) => ApprovalMatrixRequest | { departmentId: string | number; data: ApprovalMatrixRequest }
}

export const BaseApprovalMatrixModal = ({
  department,
  matrix,
  reload,
  departmentsOptions,
  departmentsLoading = false,
  users,
  usersFetching = false,
  createMutation,
  updateMutation,
  isCreating = false,
  isUpdating = false,
  errors = [],
  DeleteModalComponent,
  successMessage,
  approversTooltip,
  selectUserDropdownHint,
  transformApproversToPayload
}: Props) => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const merchantCurrency = useAppSelector(selectMerchantCurrency)
  const currencySymbol = getCurrencySymbol(formatCurrencyForDisplay(merchantCurrency))

  const [enableThresholds, setEnableThresholds] = useState(false)
  const [error, setError] = useState('')
  const [departmentId, setDepartmentId] = useState(department?.id || '')

  const defaultTransformMatrixToApprovers = (matrix: ApprovalMatrixResponse): Approvers[] => {
    return (
      matrix.approvalLevels?.map((level: ApprovalLevelResponse) => {
        const { approvalMatrixId: _, approvalsRequired, ...rest } = level
        return {
          ...rest,
          id: level.id || uuid(),
          priority: level.priority || 1,
          requiredApprovals: approvalsRequired || 1,
          approvers:
            level.approvers
              ?.map((approver: ApprovalMatrixLevelApproverResponse) => approver?.approverUser?.id)
              .filter((id): id is number => id !== undefined) || []
        }
      }) || []
    )
  }

  const defaultTransformApproversToPayload = (
    approversData: Approvers[],
    deptId?: string | number
  ): { data: ApprovalMatrixRequest; departmentId: string | number } => {
    const approvalLevels = approversData
      .map(({ id, isThresholdEnabled, amountThreshold, ...approver }) => {
        const normalizedThreshold = normalizeAmountThreshold(amountThreshold)

        return {
          ...(typeof id === 'number' && { id }),
          ...(isThresholdEnabled && normalizedThreshold !== undefined && { amountThreshold: normalizedThreshold }),
          ...approver
        }
      })
      .map(approver => ({
        ...approver,
        approvers: approver.approvers.map(approverId => ({
          merchantUserId: approverId,
          approvalWeight: 1
        }))
      }))

    return {
      data: { approvalLevels },
      departmentId: deptId!
    }
  }

  const [approversData, setApproversData] = useState<Approvers[]>(
    matrix ? defaultTransformMatrixToApprovers(matrix) : [{ ...INITIAL_APPROVERS_DATA() }]
  )

  useEffect(() => {
    if (approversData && matrix) {
      const hasValidThresholds = approversData.some(
        item => item.amountThreshold !== undefined && item.amountThreshold !== DEFAULT_THRESHOLD
      )

      if (hasValidThresholds) {
        setEnableThresholds(true)

        setApproversData(prev =>
          prev.map((p, i) => ({
            ...p,
            isThresholdEnabled: i < prev.length - 1
          }))
        )
      }
    }
  }, [matrix])

  const onSubmit = async () => {
    // Only validate department if departmentsOptions prop is provided
    if (departmentsOptions && !departmentId) {
      setError(tr('FRONTEND.BASE_APPROVAL_MATRIX_MODAL.PLEASE_SELECT_A_DEPARTMENT', 'Please select a department'))
      return
    } else {
      setError('')
    }

    const isValidThreshold = () =>
      approversData.every((approver, i) => {
        return (
          i === approversData.length - 1 ||
          !approver.isThresholdEnabled ||
          approver.amountThreshold >= approversData[i].amountThreshold
        )
      })

    if (!isValidThreshold() && enableThresholds) {
      setError(
        tr(
          'FRONTEND.USERS_MANAGEMENT.THRESHOLD_ERROR',
          'Each higher approval level must have a greater threshold than lower levels. Please update or adjust.'
        )
      )
      return
    }

    const payload = (transformApproversToPayload || defaultTransformApproversToPayload)(
      approversData,
      departmentsOptions ? departmentId : undefined
    )

    try {
      if (matrix) {
        await updateMutation(payload)
      } else {
        await createMutation(payload)
      }

      typeof reload === 'function' && reload()

      modalHide()

      // Show toast after a small delay to ensure modal is closed
      setTimeout(() => {
        if (successMessage?.title) {
          toastSuccess(successMessage.title, successMessage.subtitle)
        }
      }, 100)
    } catch (error: any) {
      if (error?.cause && Array.isArray(error.cause)) {
        const errorMap: Record<string, string> = {
          amountThreshold: tr(
            'FRONTEND.USERS_MANAGEMENT.THRESHOLD_ERROR',
            'Each higher approval level must have a greater threshold than lower levels. Please update or adjust.'
          ),
          requiredApprovals: tr(
            'FRONTEND.USERS_MANAGEMENT.REQUIRED_APPROVALS_ERROR',
            'At least one approver is required'
          )
        }

        const errorField = error.cause.find(({ field }: { field: string }) => errorMap[field])?.field

        if (errorField) setError(errorMap[errorField])
      }
    }
  }

  const onDelete = async () => {
    if (DeleteModalComponent) {
      modalShow({
        header: tr('FRONTEND.DEPARTMENTS.DELETE_MATRIX.TITLE', 'Delete Custom Approval Matrix'),
        content: <DeleteModalComponent reload={reload} department={department} />
      })
    }
  }

  const indexOfFinalApprover = useMemo(() => {
    if (approversData && users) {
      return approversData.findIndex(approverLevel =>
        approverLevel.approvers.find(approver => {
          return users.find(
            user => user.userId === approver && (user.customerRole === 'ADMINISTRATOR' || user.customerRole === 'OWNER')
          )
        })
      )
    }
    return null
  }, [approversData, users])

  const highestThresholdAmount = useMemo(() => {
    if (!enableThresholds || approversData.length === 0) return null

    const thresholds = approversData
      .slice(0, -1) // Exclude the final approver
      .map(approver => normalizeAmountThreshold(approver.amountThreshold))
      .filter(
        (threshold): threshold is number =>
          threshold !== undefined && threshold !== DEFAULT_THRESHOLD && !Number.isNaN(threshold)
      )

    return thresholds.length > 0 ? Math.max(...thresholds) : null
  }, [approversData, enableThresholds])

  const getUserOptions = (index: number, currentApprovers: number[]) => {
    const usersSelectedInOtherLevels = approversData
      .filter((_, i) => i !== index)
      .flatMap(approver => approver.approvers)

    return (
      users
        ?.map(user => {
          const { firstName, lastName, email, userId } = user || {}
          const label = firstName ? `${firstName} ${lastName || ''}` : email
          const isFinalApprover = approversData.length === index + 1
          const isCurrentApprover = currentApprovers.includes(userId)
          const isSelectedInOtherLevel = usersSelectedInOtherLevels.includes(userId)

          if (isCurrentApprover && !isFinalApprover) {
            return { label, value: userId }
          }
          if (isSelectedInOtherLevel) {
            return null
          }
          return { label, value: userId }
        })
        ?.filter(value => value !== null) || []
    )
  }

  const handleAddApprover = () => {
    const newApproversData = approversData.map(approver => ({
      ...approver,
      priority: approver.priority + 1
    }))
    newApproversData.unshift({ ...INITIAL_APPROVERS_DATA() })
    setApproversData(newApproversData)
  }

  const handleDeleteApprover = (index: number) => {
    const newApproversData = [...approversData]
    newApproversData.splice(index, 1)
    const recalculated = recalculatePriorities(newApproversData)

    setApproversData(
      recalculated.map((approver, i) => ({
        ...approver,
        isThresholdEnabled: i < recalculated.length - 1 ? approver.isThresholdEnabled : false
      }))
    )
  }

  const handleApproverChange = (index: number, value: SelectValue | SelectValue[]) => {
    const newApproversData = [...approversData]
    const valueAsNumberArray = normalizeToNumberArray(value)
    console.log('valueAsNumberArray:', valueAsNumberArray)

    newApproversData[index].approvers = valueAsNumberArray
    setApproversData(newApproversData)
  }

  return (
    <div className='base-approval-matrix-modal'>
      <div className='base-approval-matrix-container'>
        {departmentsOptions && (
          <CoreSelect
            type='default'
            data-test='departmentId'
            loading={departmentsLoading}
            isAccountsLoading={departmentsLoading}
            options={departmentsOptions}
            label={tr('FRONTEND.USERS_MANAGEMENT.DEPARTMENT', 'Department')}
            value={departmentId}
            onChange={(_name: string, value: string | number) => setDepartmentId(value)}
            disabled={departmentsLoading || !!matrix}
          />
        )}

        <div className='approvers-container'>
          <div className='approvers-container-header'>
            <div className='approvers-container-header-title'>
              <span>{tr('FRONTEND.DEPARTMENTS.COLUMN.APPROVERS', 'Approvers')}</span>
              {approversTooltip}
            </div>
            <CoreButton
              size='normal'
              text={tr('FRONTEND.USERS_MANAGEMENT.NEW_APPROVER', 'New Approver')}
              disabled={approversData.length >= 5}
              onClick={handleAddApprover}
              variation='elevated'
              LeftIcon={<PlusIcon />}
            />
          </div>

          <div className='approvers-container-body'>
            {approversData
              .sort((a, b) => a.priority - b.priority)
              .map(({ priority, amountThreshold, approvers, isThresholdEnabled, id }, index) => {
                return (
                  <div className='approver-row' key={id}>
                    <div className='threshold-container'>
                      <span className='threshold-container-title'>
                        {approversData.length === index + 1
                          ? tr('FRONTEND.BASE_APPROVAL_MATRIX_MODAL.FINAL', 'Final')
                          : NUMBER_MAP[priority]}{' '}
                        {tr('FRONTEND.TRANSACTION_MATRIX.APPROVER', 'Approver')}
                      </span>

                      {enableThresholds && index < approversData.length - 1 && (
                        <Threshold
                          isThresholdEnabled={isThresholdEnabled}
                          amountThreshold={amountThreshold}
                          onChange={() => {}}
                          index={index}
                          setApproversData={setApproversData}
                          currencySymbol={currencySymbol}
                        />
                      )}

                      {enableThresholds && index === approversData.length - 1 && highestThresholdAmount !== null && (
                        <div className='threshold-input-container threshold-above'>
                          <span className='threshold-input-container-title'>
                            {tr('FRONTEND.TRANSACTION_MATRIX.ABOVE', 'Above')} {currencySymbol}
                            {highestThresholdAmount.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className='approver-select-container'>
                      <CoreSelect
                        type='default'
                        label={tr('FRONTEND.BASE_APPROVAL_MATRIX_MODAL.SELECT_USER', 'Select User')}
                        isMultiply={true}
                        name='approverUser'
                        loading={usersFetching}
                        value={approvers || []}
                        disabled={usersFetching}
                        onChange={(_name: string, value: SelectValue | SelectValue[]) =>
                          handleApproverChange(index, value as SelectValue | SelectValue[])
                        }
                        options={getUserOptions(index, approvers)}
                        dropdownHint={selectUserDropdownHint}
                      />
                      {indexOfFinalApprover === index && approversData.length && index !== approversData.length - 1 && (
                        <div className='final-approver-container'>
                          <span>
                            {tr(
                              'FRONTEND.USERS_MANAGEMENT.FINAL_APPROVER',
                              'The approval flow ends at this step. Since this role has final approval rights, any subsequent approvers in the matrix will be skipped.'
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                    {index < approversData?.length - 1 && (
                      <div className='delete-icon-container'>
                        <DeleteIcon width={24} height={24} onClick={() => handleDeleteApprover(index)} />
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
          <CoreCheckbox
            className='enable-thresholds-checkbox'
            type='check'
            value='enableThresholds'
            checked={enableThresholds}
            onClick={val => {
              if (val) {
                setApproversData(prev =>
                  prev.map(p => ({
                    ...p,
                    isThresholdEnabled: false,
                    amountThreshold: undefined
                  }))
                )
              } else {
                setApproversData(prev =>
                  prev.map(p => ({
                    ...p,
                    isThresholdEnabled: false,
                    amountThreshold: undefined
                  }))
                )
              }
              setEnableThresholds(val)
            }}
            label={tr('FRONTEND.USERS_MANAGEMENT.ENABLE_THRESHOLDS', 'Enable Thresholds')}
          />
        </div>

        <ErrorDisplay error={[error, ...errors]} />
        <CoreModalDialogFooter>
          <div className='base-approval-matrix-modal-footer'>
            {matrix ? (
              <>
                {DeleteModalComponent ? (
                  <CoreButton
                    size='large'
                    text={tr('FRONTEND.USERS_MANAGEMENT.DELETE_MATRIX', 'Delete Matrix')}
                    variation='tertiary'
                    onClick={onDelete}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                  />
                ) : (
                  <CoreButton
                    size='large'
                    text={tr('FRONTEND.USERS_MANAGEMENT.CANCEL', 'Cancel')}
                    variation='tertiary'
                    onClick={modalHide}
                    isLoading={isCreating}
                    disabled={isCreating}
                  />
                )}
                <CoreButton
                  size='large'
                  text={tr('FRONTEND.COOKIE_CONSENT.SAVE', 'Save')}
                  fullWidth
                  onClick={onSubmit}
                  isLoading={isUpdating}
                  disabled={isUpdating}
                />
              </>
            ) : (
              <>
                <CoreButton
                  size='large'
                  text={tr('FRONTEND.USERS_MANAGEMENT.CANCEL', 'Cancel')}
                  variation='tertiary'
                  onClick={modalHide}
                  isLoading={isCreating}
                  disabled={isCreating}
                />
                <CoreButton
                  size='large'
                  variation='primary-black'
                  text={tr('FRONTEND.USERS_MANAGEMENT.CREATE', 'Create')}
                  fullWidth
                  onClick={onSubmit}
                  isLoading={isCreating}
                  disabled={isCreating}
                />
              </>
            )}
          </div>
        </CoreModalDialogFooter>
      </div>
    </div>
  )
}
